import common from '../commons/commons';
import { Themes, STATE_TINT_ALPHA, parseColor, blend, stateInk } from '../commons/colors';

/* ------------------------------------------------------------------------------------
 * Calm-density dialect (SS05902) for the workflow diagram.
 *
 * cytoscape paints to a canvas, so it cannot read the theme's CSS custom properties: the
 * greys come from the JS mirror the charts already use, and the brand shades are the ones
 * the app's hover / selected washes resolve to.
 *
 * A state is drawn as the card state chip — FTSState's tint variant: the state's own hue at
 * STATE_TINT_ALPHA as the fill, stateInk() of that hue as the text. One state dialect on
 * cards, list rows, history and here. The fill is pre-blended over the surface rather than
 * drawn translucent, so an edge passing behind a node never shows through it.
 * ---------------------------------------------------------------------------------- */
const FONT_FAMILY = 'Inter, Roboto, "Segoe UI", Helvetica, Arial, sans-serif';
export const DIM_OPACITY = 0.26;
// id of the built-in create activity, when a caller lists it among the activities
export const CREATE_ACTIVITY_ID = "create";

export const palette = (dark) => {
  const gray = dark ? Themes.dark : Themes.light;
  return {
    surface: dark ? [33, 33, 33] : [252, 252, 252],   // --fts-card-background
    ground: dark ? '#212121' : '#fcfcfc',
    line: gray.gray200,
    ink: dark ? gray.gray800 : gray.gray700,
    muted: dark ? gray.gray500 : gray.gray400,
    edge: gray.gray400,
    brand: '#2b9ec6',                                  // --fts-primary
    brandText: dark ? '#7cc8e3' : '#1e6f8b',
    brandWash: dark ? '#222c2f' : '#f3fafd',           // --fts-primary-hover
    brandLine: dark ? '#24566a' : '#a7daec'
  };
};

const isDarkTheme = () =>
  typeof document !== 'undefined' && !!document.body && document.body.classList.contains('dark');

// The state chip's two colours. An unset or unparseable colour falls back to the neutral
// FTSState falls back to (--fts-default).
export const stateChip = (color, dark) => {
  const c = palette(dark);
  const hue = parseColor(color) ? color : c.muted;
  const ink = stateInk(hue);
  return {
    fill: `rgb(${blend(parseColor(hue), c.surface, STATE_TINT_ALPHA).join(', ')})`,
    ink: dark ? ink.dark : ink.light
  };
};

// Edges of one bundle carry `labelAt` (see spreadBundledLabels): how far along the edge,
// from its source, the label sits. cytoscape wants that as a distance from the source
// endpoint, and an edge runs border to border — so a chip's height comes off the distance
// between the two centres first.
// cytoscape evaluates this when the elements are added, i.e. BEFORE the layout has placed
// them (every node still at 0,0), and not again when they move: whoever runs a layout must
// call cy.style().update() after it, or every bundled label sits on its source node.
const NODE_HEIGHT = 28;
const MIN_LABEL_OFFSET = 28;
const bundledLabelOffset = (ele) => {
  const from = ele.source().position(), to = ele.target().position();
  const length = Math.hypot(to.x - from.x, to.y - from.y) - NODE_HEIGHT;
  return Math.max(MIN_LABEL_OFFSET, length * ele.data("labelAt"));
};

let getStyle = function (dark = isDarkTheme()) {
  const c = palette(dark);
  return [
    {
      selector: "node",
      style: {
        shape: "round-rectangle",
        width: "label",
        height: 8,
        padding: "10px",
        content: "data(name)",
        "text-valign": "center",
        "font-family": FONT_FAMILY,
        "font-size": 13,
        "font-weight": 500,
        opacity: "data(opacity)"
      }
    },
    {
      selector: ".normal",
      style: {
        "background-color": ele => stateChip(ele.data("color"), dark).fill,
        color: ele => stateChip(ele.data("color"), dark).ink,
        "border-width": 0,
        "border-color": ele => stateChip(ele.data("color"), dark).ink
      }
    },
    {
      // start / any / change: a hairline ring with a caption, not a 60px disc
      selector: ".special",
      style: {
        shape: "ellipse",
        width: 11,
        height: 11,
        padding: 0,
        content: "data(caption)",
        "background-color": c.ground,
        "border-width": 1.5,
        "border-color": c.muted,
        "text-halign": "right",
        "text-valign": "center",
        "text-margin-x": 5,
        "font-size": 10.5,
        "font-weight": 400,
        color: c.muted
      }
    },
    {
      selector: ".special[id = 'any']",
      style: {
        "border-style": "dashed"
      }
    },
    {
      selector: "node:selected",
      style: {
        "border-width": 1.5,
        "border-color": c.ink
      }
    },
    {
      selector: "edge",
      style: {
        opacity: "data(opacity)",
        width: 1.25,
        "line-color": c.edge,
        "target-arrow-shape": "triangle",
        "target-arrow-color": c.edge,
        "source-arrow-shape": "none",
        "arrow-scale": 0.9,
        // the activity chip: ground fill, hairline border — and it takes events, so the
        // chip (not a 1px line) is the hover target
        label: "data(label)",
        "font-family": FONT_FAMILY,
        "font-size": 11.5,
        color: c.ink,
        "text-opacity": 1,
        "text-background-color": c.ground,
        "text-background-shape": "roundrectangle",
        "text-background-opacity": 1,
        "text-background-padding": 4,
        "text-border-opacity": 1,
        "text-border-width": 1,
        "text-border-color": c.line,
        "text-border-style": "solid",
        "text-events": "yes",
        "control-point-step-size": "90px",
        // a top-to-bottom layout brings edges in from above and out below: a loop on the
        // left stays clear of them (at 60deg its chip landed on the incoming edge's chip)
        'loop-direction': '-90deg',
        'loop-sweep': '-60deg',
        "curve-style": "data(curveStyle)",
        "segment-distances": "data(cyedgebendeditingDistances)",
        "segment-weights": "data(cyedgebendeditingWeights)",
      }
    },
    {
      selector: "edge[labelAt]",
      style: {
        label: "",
        "source-label": "data(label)",
        "source-text-offset": bundledLabelOffset
      }
    },
    {
      selector: "edge.option",
      style: {
        "line-style": "dashed"
      }
    },
    // hover: what is lit turns brand, the rest steps back
    {
      selector: ".is-dim",
      style: {
        opacity: DIM_OPACITY
      }
    },
    {
      selector: "edge.is-hot",
      style: {
        width: 1.75,
        "line-color": c.brand,
        "target-arrow-color": c.brand,
        color: c.brandText,
        "text-background-color": c.brandWash,
        "text-border-color": c.brandLine,
        "z-index": 10
      }
    },
    {
      selector: "edge.is-pinned",
      style: {
        "text-border-color": c.brand
      }
    },
    {
      selector: "node.normal.is-hot",
      style: {
        "border-width": 1
      }
    },
    // some style for the extension
    {
      selector: ".eh-handle",
      style: {
        "background-color": "red",
        content: "+",
        width: 8,
        height: 8,
        shape: "ellipse",
        "overlay-opacity": 0,
        "border-width": 8, // makes the handle easier to hit
        "border-opacity": 0
        //"padding-top": 15,
        //"padding-bottom": 15,
      }
    },
    {
      selector: ".eh-hover",
      style: {
        "background-color": "red"
      }
    },
    {
      selector: ".eh-source",
      style: {
        "border-width": 2,
        "border-color": "red"
      }
    },
    {
      selector: ".eh-target",
      style: {
        "border-width": 2,
        "border-color": "red"
      }
    },
    {
      selector: ".eh-preview, .eh-ghost-edge",
      style: {
        "background-color": "red",
        "line-color": "red",
        "target-arrow-color": "red",
        "source-arrow-color": "red"
      }
    },
    {
      selector: ".eh-ghost-edge.eh-preview-active",
      style: {
        opacity: 0
      }
    }
  ];
}

var findPosition = function (diagram, id) {
  var node = diagram.find(x => x.id == id);
  if (node) {
    return node.position;
  }

  return {}
}

let getNodes = function (states, flows, selection, designer) {
  let nodes = [];
  if (designer) {
    nodes = designer.nodes;
  }


  var specials = [
    {
      id: "start",
      name: "start",
      caption: "start",
      classes: "special"
    },
    {
      id: "any",
      name: "any",
      caption: "any state",
      classes: "special"
    },
    {
      id: "change",
      name: "change",
      caption: "change status",
      classes: "special"
    }
  ];

  let result = (specials.concat(states))
    .map(function (x) {
      x.position = findPosition(nodes, x.id);

      return {
        id: x.id,
        name: x.name,
        data: x,
        classes: x.classes ? x.classes : "normal",
        position: findPosition(nodes, x.id),
      };
    })


  let nodeIds = [];
  if (selection) {
    if (selection.type == 'activity') {
      let selectedFlows = flows.filter(x => x.activityId == selection.item);
      selectedFlows.forEach(flow => {
        nodeIds.push(flow.fromStateId);
        if (flow.conditional) {
          nodeIds = nodeIds.concat(flow.conditionalStates.map(x => x.toStateId));
        } else {
          nodeIds.push(flow.toStateId);
        }
      });
    } else if (selection.type == 'state') {
      nodeIds.push(selection.item);
    }
  }

  result.forEach(node => {
    node.data.opacity = 1;



    if (selection && selection.item && selection.type) {
      node.data.opacity = 0.4;
      if (nodeIds.some(x => node.data.id == x)) {
        node.data.opacity = 1;
      }
    }
  });

  return result;
}

let getEdges = function (activities, states, flows, selection, diagram) {
  if (!flows) flows = [];

  var directs = flows
    .filter(function (x) {
      return !x.conditional;
    })
    .map(function (x) {
      return {
        data: {
          id: x.id,
          flowId: x.id,
          flow: x,
          activityId: x.activityId,
          source: x.fromStateId,
          target: x.toStateId ? x.toStateId : 'any'
        },
      };
    });

  if (states.some(x => x.initial)) {
    // The start edge is synthetic. When the caller supplies the built-in create activity
    // (id "create"), the edge carries it, so hovering "create" can show the create form.
    const create = activities.find(x => x.id == CREATE_ACTIVITY_ID);
    directs.push({
      data: {
        id: common.nanoID(),
        source: 'start',
        target: states.find(x => x.initial).id,
        ...(create ? { activityId: create.id } : {})
      }
    })
  }

  // a state is "only through change" when no flow leaves it and none arrives — and a
  // conditional flow arrives through its conditionalStates, not its own toStateId
  var onlyThroughChanges = states.filter(x =>
    !flows.some(y => y.fromStateId == x.id)
    && !flows.some(y => y.toStateId == x.id)
    && !flows.some(y => y.conditional
      && (y.conditionalStates || []).some(z => z.toStateId == x.id)));

  onlyThroughChanges.forEach(change => {
    directs.push({
      data: {
        id: common.nanoID(),
        source: 'change',
        label: 'change',
        target: change.id
      }
    })
  });


  var conditionals = flows
    .filter(function (x) {
      return x.conditional;
    })
    .map(function (x) {
      var result = x.conditionalStates.map(function (y) {
        return {
          data: {
            id: y.id,
            flowId: x.id,
            flow: x,
            activityId: x.activityId,
            source: x.fromStateId,
            target: y.toStateId,
            conditional: true
          },
          classes: "option"
        };
      });
      return result;
    })
    .reduce(function (a, b) {
      return a.concat(b);
    }, []);

  var edges = directs.concat(conditionals);
  edges.forEach(function (x) {
    x.data.curveStyle = 'bezier';
    if(x.data.cyedgebendeditingDistances
      && x.data.cyedgebendeditingDistances.length > 0) {
        x.data.curveStyle = 'segments';
    }

    if (!x.data.label) {
      x.data.label = "create";
    }

    x.data.opacity = 0.95;

    if (selection && selection.type && selection.item) {
      x.data.opacity = 0.15;
    }

    if (x.data.activityId == null && x.data.source == null) {
      x.data.source = "start";
    } else if (x.data.activityId != null && x.data.source == null) {
      x.data.source = "any";
    }

    var activity = activities.find(function (activity) {
      return activity.id == x.data.activityId;
    });

    if (activity) {
      x.data.activity = activity;

      if (selection) {
        switch (selection.type) {
          case 'information':
            if (activity.classification.some(x => x.id == selection.item)) {
              x.data.opacity = 0.95;
            }
            break;

          case 'activity':
            if (activity.id == selection.item) {
              x.data.opacity = 0.95;
            }
            break;

          case 'state':
            if (x.data.source == selection.item) {
              x.data.opacity = 0.95;
            }
            break;
        }
      }



      if (activity.name) {
        x.data.label =
          (activity.emoji ? activity.emoji + " " : "") + activity.name;
      }
    }
  });


  let diagramEdges = [];
  if (diagram && diagram.edges) {
    diagramEdges = diagram.edges;
  }

  edges.forEach((edge) => {
    let diagramEdge = diagramEdges.find(x => x.id == edge.data.id 
      || (x.source == edge.data.source && x.target == edge.data.target));
    if(diagramEdge) {
      edge.data.cyedgebendeditingDistances = diagramEdge.cyedgebendeditingDistances;
      edge.data.cyedgebendeditingWeights = diagramEdge.cyedgebendeditingWeights;
      if(edge.data.cyedgebendeditingDistances
        && edge.data.cyedgebendeditingDistances.length > 0) {
          edge.data.curveStyle = 'segments';
      }
    }
  });


  // edges[0].data.curveStyle = 'segments';
  // edges[0].data.cyedgebendeditingDistances = [127.3186651296108]
  // edges[0].data.cyedgebendeditingWeights = [0.8341858178901047]

  spreadBundledLabels(edges);

  return edges;
}

/* Edges between the same two states are drawn as one bundle of curves a few pixels apart, so
   their midpoint labels land on top of each other ("Resume Work" over "On Hold"). Give each
   edge of a bundle its own slot along the A–B axis; `labelAt` is that slot measured from the
   edge's own source, which for an A⇄B pair comes out as the source third of each. */
let spreadBundledLabels = function (edges) {
  const bundles = {};
  edges.forEach(edge => {
    const { source, target } = edge.data;
    if (source == null || target == null || source == target) return;
    const ends = [String(source), String(target)].sort();
    const key = JSON.stringify(ends);
    (bundles[key] = bundles[key] || { first: ends[0], edges: [] }).edges.push(edge);
  });

  Object.keys(bundles).forEach(key => {
    const bundle = bundles[key];
    if (bundle.edges.length < 2) return;
    // slots run along the axis from `first`; edges leaving `first` take the near slots and
    // the returning ones the far slots, so every label sits nearer its own source
    const leaves = edge => String(edge.data.source) == bundle.first;
    const ordered = bundle.edges.filter(leaves).concat(bundle.edges.filter(edge => !leaves(edge)));
    ordered.forEach((edge, index) => {
      const along = (index + 1) / (ordered.length + 1);
      edge.data.labelAt = leaves(edge) ? along : 1 - along;
    });
  });
}

/* Nodes and edges ready for cytoscape: edges whose endpoints do not exist are dropped (they
   would throw), and — for the read-only diagrams — so are the start / any / change
   pseudo-states that nothing connects to. The Studio keeps all three: it saves a position for
   every node, and a pseudo-state that appears later must still have one. */
let getElements = function (activities, states, flows, selection, diagram, options) {
  const hideOrphans = !!(options && options.hideOrphans);
  let nodes = getNodes(states, flows, selection, diagram);
  const edges = getEdges(activities, states, flows, selection, diagram).filter(edge => {
    const sourceExists = nodes.some(node => node.data.id === edge.data.source);
    const targetExists = nodes.some(node => node.data.id === edge.data.target);
    if (!sourceExists || !targetExists) {
      console.warn(`Edge ${edge.data.id} has a non-existent source (${edge.data.source}) or target (${edge.data.target})`);
    }

    return sourceExists && targetExists;
  });

  if (hideOrphans) {
    nodes = nodes.filter(node => node.classes !== "special"
      || edges.some(edge => edge.data.source === node.data.id || edge.data.target === node.data.id));
  }

  return { nodes, edges };
}

/* Which elements light up for a hover. `hot` is one of
     { kind: 'activity', id }       every flow of that activity + the states they join
     { kind: 'edge', id }           one edge that carries no activity (start / change)
     { kind: 'state', id }          the state + the flows leaving it + where they lead
     { kind: 'information', name }  every flow whose activity's form holds that field
   Returns the ids to keep lit; everything else dims. Pure, so the rule is testable without
   a canvas. */
let getHighlight = function (edges, hot) {
  const lit = { edges: [], nodes: [] };
  if (!hot) return lit;

  const matches = edge => {
    const data = edge.data;
    switch (hot.kind) {
      case 'activity': return data.activityId != null && data.activityId == hot.id;
      case 'edge': return data.id == hot.id;
      case 'state': return data.source == hot.id;
      case 'information': return fieldNames(data.activity).indexOf(String(hot.name).toLowerCase()) > -1;
      default: return false;
    }
  };

  if (hot.kind === 'state') lit.nodes.push(hot.id);
  edges.filter(matches).forEach(edge => {
    lit.edges.push(edge.data.id);
    [edge.data.source, edge.data.target].forEach(id => {
      if (lit.nodes.indexOf(id) === -1) lit.nodes.push(id);
    });
  });

  return lit;
}

// lower-cased names of the information an activity's form holds
let fieldNames = function (activity) {
  const fields = activity && Array.isArray(activity.fields) ? activity.fields : [];
  return fields
    .map(f => (f == null ? "" : typeof f === "string" ? f : f.name || f.fieldName || f.displayName || ""))
    .filter(Boolean)
    .map(x => x.toLowerCase());
}

/* Where the form card goes. It is anchored to the hovered chip, never the mouse: beside it
   (right, then left), above or below — whichever stays inside the graph and hides the least
   of what is lit, because the card must not cover the path it explains. Boxes are
   { left, top, width, height } in the graph container's pixels. Pure, so it is testable. */
const CARD_GAP = 10;
const CARD_MARGIN = 8;
let placeCard = function ({ anchor, card, container, keepClear }) {
  const clamp = (value, size, limit) =>
    Math.max(CARD_MARGIN, Math.min(value, limit - CARD_MARGIN - size));
  const xs = [
    anchor.left + anchor.width + CARD_GAP,
    anchor.left - CARD_GAP - card.width,
    clamp(anchor.left + anchor.width / 2 - card.width / 2, card.width, container.width)
  ];
  const ys = [
    clamp(anchor.top - CARD_GAP, card.height, container.height),
    anchor.top - CARD_GAP - card.height,
    anchor.top + anchor.height + CARD_GAP,
    clamp(anchor.top + anchor.height + CARD_GAP - card.height, card.height, container.height)
  ];
  const overlap = (left, top, box) =>
    Math.max(0, Math.min(left + card.width, box.left + box.width) - Math.max(left, box.left))
    * Math.max(0, Math.min(top + card.height, box.top + box.height) - Math.max(top, box.top));

  let best = null;
  xs.forEach(left => ys.forEach(top => {
    const inside = left >= CARD_MARGIN && top >= CARD_MARGIN
      && left + card.width <= container.width - CARD_MARGIN
      && top + card.height <= container.height - CARD_MARGIN;
    if (!inside || overlap(left, top, anchor) > 0) return;
    const hidden = (keepClear || []).reduce((sum, box) => sum + overlap(left, top, box), 0);
    if (!best || hidden < best.hidden) best = { left, top, hidden };
  }));

  // nothing fits (a graph smaller than the card): stay beside the chip, clamped inside
  if (!best) {
    best = {
      left: clamp(xs[0], card.width, container.width),
      top: clamp(anchor.top - CARD_GAP, card.height, container.height)
    };
  }

  return { left: Math.round(best.left), top: Math.round(best.top) };
}

/* The automatic layout, for a workflow nobody has arranged by hand (a saved diagram uses
   "preset"). dagre knows nothing of edge labels, so the spacing has to leave room for the
   activity chips: generous for a small workflow, where they would otherwise collide — and
   tight for a large one, which the generous spacing spreads so wide (a real 16-state module:
   4,100px) that fitting it shrinks every label past reading. */
const DENSE_STATES = 8;
const DENSE_EDGES = 12;
let getLayout = function (elements) {
  const nodes = (elements && elements.nodes) || [];
  const edges = (elements && elements.edges) || [];
  const states = nodes.filter(node => node.classes !== "special").length;
  const dense = states > DENSE_STATES || edges.length > DENSE_EDGES;
  return {
    name: "dagre",
    ranker: "network-simplex",
    rankDir: "TB",
    nodeSep: dense ? 40 : 70,
    rankSep: dense ? 90 : 100,
    edgeSep: dense ? 30 : 50
  };
}

/* What api/workspace/diagram/{id} answers, as the diagramData the diagram components take.
   `information` and each activity's `fields` are what the form card shows; a backend that does
   not send them yet leaves `information` undefined, and the card then shows the activity and
   its path only. The built-in create activity is part of no flow, so it rides the start edge
   (see getEdges) — that is how "create" can show the create form. */
let fromDiagramPayload = function (data, module) {
  data = data || {};
  const activities = (data.activities || []).slice();
  if (data.create) {
    activities.push({ ...data.create, id: CREATE_ACTIVITY_ID, name: data.create.name || "create" });
  }

  return {
    name: (module && module.name) || "Workflow",
    emoji: (module && module.emoji) || null,
    states: data.states || [],
    flows: data.flows || [],
    activities,
    diagram: data.diagram,
    information: Array.isArray(data.information) ? data.information : undefined,
  };
}

export default {
  getStyle,
  getLayout,
  fromDiagramPayload,
  getNodes,
  getEdges,
  getElements,
  getHighlight,
  fieldNames,
  placeCard,
}